// Workstream: accounts + photo cards. Shrink photos in the browser before they are uploaded.

export const PHOTO_MAX_SIDE = 1024;
/** The server takes up to 600 KB; aim a little under. */
const TARGET_BYTES = 560 * 1024;

type Source = ImageBitmap | HTMLImageElement;

async function decode(file: Blob): Promise<Source> {
  if (typeof createImageBitmap === 'function') {
    try {
      // Honour the phone's EXIF rotation so portrait photos stay upright.
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall back to an <img>, which also applies EXIF orientation in modern browsers.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function size(source: Source): [number, number] {
  return source instanceof HTMLImageElement ? [source.naturalWidth, source.naturalHeight] : [source.width, source.height];
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/** Resize to at most 1024px on the long side and re-encode as JPEG, stepping quality down if needed. */
export async function resizePhoto(file: File): Promise<Blob> {
  if (file.type && !file.type.startsWith('image/')) throw new Error('That file is not a photo.');
  let source: Source;
  try {
    source = await decode(file);
  } catch {
    throw new Error('Could not open that photo. Try a JPEG or PNG.');
  }
  try {
    const [w, h] = size(source);
    if (!w || !h) throw new Error('Could not open that photo. Try a JPEG or PNG.');
    let scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(w, h));
    for (let attempt = 0; attempt < 5; attempt++) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('This browser cannot resize photos.');
      // JPEG has no transparency; give see-through PNGs a white background instead of black.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      for (const quality of [0.85, 0.72, 0.6]) {
        const blob = await toBlob(canvas, quality);
        if (blob && blob.size <= TARGET_BYTES) return blob;
      }
      scale *= 0.8;
    }
    throw new Error('That photo is too detailed to shrink. Try another one.');
  } finally {
    if (!(source instanceof HTMLImageElement)) source.close();
  }
}

/** The image id behind a card's /api/images/:id url. */
export function imageIdFromUrl(url: string | undefined): string | null {
  const m = url?.match(/^\/api\/images\/(\d+)$/);
  return m ? m[1] : null;
}
